import { memo as Memo, useMemo, useEffect, useCallback } from 'react';
import { gsap, TweenLite, TimelineMax, Bounce, CSSPlugin } from 'gsap';

//* Components
import Image from '@components/global/Image';

//* Images
import stalin from '../../../../../public/images/animation/stalin.png';
import lenin from '../../../../../public/images/animation/lenin.png';

//* Styles
import TextHoverAnimationsStyle from './style';

const TextHoverAnimations = Memo((props) => {
	//! Animation
	const goodAnim = useMemo(() => new TimelineMax({ paused: true }), []);
	const sovietAnim = useMemo(() => new TimelineMax({ paused: true, repeat: -1, repeatDelay: 0.2 }), []);
	const transformAnim = useMemo(() => new TimelineMax({ paused: true }), []);
	const lineAnim = useMemo(() => new TimelineMax({ paused: true }), []);
	const depressAnim = useMemo(() => new TimelineMax({ paused: true }), []);
	const starAnim = useMemo(() => new TimelineMax({ paused: true, repeat: -1, yoyo: true, ease: 'none' }), []);
	const creativeAnim = useMemo(() => new TimelineMax({}), []);
	const expAnim = useMemo(() => new TimelineMax({ paused: true, repeat: -1, repeatDelay: 0.3 }), []);

	useEffect(() => {
		//! Good animation
		goodAnim.set('.good-txt', { text: 'goood' }).set('.good-txt', { text: 'gooood', delay: 0.05 }).set('.good-txt', { text: 'goooood', delay: 0.1, repeat: -1 });

		//! Soviet Animation
		const sovietDelay = '+=0.2';

		sovietAnim
			.set('.sovietIm', { opacity: 0 }, sovietDelay)
			.set('.soviet-1', { opacity: 1 })
			.set('.sovietIm', { opacity: 0 }, sovietDelay)
			.set('.soviet-2', { opacity: 1 })
			.set('.sovietIm', { opacity: 0 }, sovietDelay)
			.set('.soviet-3', { opacity: 1 })
			.set('.sovietIm', { opacity: 0 }, sovietDelay)
			.set('.soviet-4', { opacity: 1 });

		//! Transform Animation
		transformAnim.to('.transform-g', 0.6, {
			attr: {
				style: 'transform: rotateX(50deg)',
			},
			ease: Bounce.easeOut,
		});

		//! Line Animation
		lineAnim.to('.lineMove', 1.5, { strokeDashoffset: 0, ease: 'none' });

		//! Depress Animation
		depressAnim.to('.depress2', 0.3, { y: '+=10' }).to('.depress3', 0.6, { y: '+=20' }, 0).to('.depress4', 0.9, { y: '+=30', ease: Bounce.easeOut }, 0);

		//! Cheerful Stars Animation
		TweenLite.set('.star', { opacity: 0, scale: 0 });
		starAnim
			.to('.star1', gsap.utils.random(0.4, 0.6), { transformOrigin: 'center', rotate: gsap.utils.random(-30, 30), scale: gsap.utils.random(0.8, 1) }, gsap.utils.random(0, 0.4))
			.to('.star2', gsap.utils.random(0.4, 0.6), { transformOrigin: 'center', rotate: gsap.utils.random(-30, 30), scale: gsap.utils.random(1, 1.3) }, gsap.utils.random(0, 0.4))
			.to('.star3', gsap.utils.random(0.4, 0.6), { transformOrigin: 'center', rotate: gsap.utils.random(-30, 30), scale: gsap.utils.random(1.5, 1.8) }, gsap.utils.random(0, 0.4));

		//! Creative Design Animation
		for (let i = 1; i <= 15; i++) {
			creativeAnim.to(`.crde-${i}`, 0.3, { attr: { rotate: i % 2 === 0 ? gsap.utils.random(5, 10) : gsap.utils.random(-5, -10) } }, 0);
		}

		//! Experiences Animation
		expAnim
			.set('.ex-txt', { text: 'digital' })
			.set('.ex-txt', { text: 'brand' }, '+=0.3')
			.set('.ex-txt-tr', { attr: { x: '+=50' } }, '+=0.6')
			.set('.ex-txt', { text: 'product' }, '+=0')
			.set('.ex-txt-trp', { x: '50' }, '+=0');
	}, []);

	//! Good Animation MouseEnter/MouseMove
	const animateGood = useCallback((reverse = false) => {
		reverse ? goodAnim.reverse() : goodAnim.play();
	}, []);

	//! Soviet Animation MouseEnter/MouseMove
	const animateSoviet = useCallback((e, paused = false) => {
		if (!paused) {
			TweenLite.to('.txt-cr', 0.2, { left: e.clientX, top: e.clientY });
			sovietAnim.play();
		} else {
			TweenLite.set('.sovietIm', { opacity: 0 });
			sovietAnim.pause();
		}
	}, []);

	//! Transform Animation MouseEnter/MouseMove
	const animateTransform = useCallback((reverse = false) => {
		reverse ? transformAnim.reverse() : transformAnim.play();
	}, []);

	//! Line Animation MouseEnter/MouseMove
	const animateLine = useCallback((reverse = false) => {
		reverse ? lineAnim.reverse() : lineAnim.play();
	}, []);

	//! Depress Animation MouseEnter/MouseMove
	const animateDepress = useCallback((reverse = false) => {
		reverse ? depressAnim.reverse() : depressAnim.play();
	}, []);

	//! Cheerful Stars Animation MouseEnter/MouseMove
	const animateStar = useCallback((reverse = false) => {
		if (!reverse) {
			TweenLite.to('.star', { opacity: 1, scale: gsap.utils.random(0.8, 1) });
			starAnim.play();
		} else {
			starAnim.reverse();
			TweenLite.to('.star', 0.4, { opacity: 0, scale: 0 });
			// starAnim.progress(0)
		}
	}, []);

	//! Creative Design Animation MouseEnter/MouseMove
	const animateCreative = useCallback((paused = false) => {
		if (paused) {
			creativeAnim.play();
		} else {
			creativeAnim.reverse();
		}
	}, []);

	//! Experiences Animation MouseEnter/MouseMove
	const animateExp = useCallback((paused = false) => {
		paused ? expAnim.pause() : expAnim.play();
	}, []);

	return (
		<TextHoverAnimationsStyle className={`crHide textHoverAnimations`}>
			<div className={`txt-cr txtCr`}>
				<svg viewBox='0 0 119.9 130.48'>
					<path
						className='sovietIm soviet-1'
						style={{ opacity: 0 }}
						d='M110.52,93.86l9.33,8.75-13.13,12.94-8.56-9.31c-25.9,14.7-49.49,12.13-71.28-7.17L8.66,114.72l-3.13-3.67c-1.69-2-3.35-4-5.06-6-.61-.7-.67-1.2.08-1.84Q12.7,92.88,24.83,82.47c.41-.35.88-.64,1.18-.86,3.14,2.42,6.05,4.89,9.19,7a56.21,56.21,0,0,0,28.07,9.52c7.43.54,14-1.66,20.6-4.49a3.83,3.83,0,0,0,.6-.43L49.55,58.64C47,61.1,44.51,63.57,42,66c-.86.84-1.75,2.24-2.66,2.26S37.54,66.87,36.7,66q-5.57-5.6-11.1-11.22c-.25-.26-.48-.55-.8-.91,1.27-1.16,2.55-2.23,3.72-3.41,4.41-4.41,8.75-8.88,13.19-13.27,2.51-2.48,5.12-4.88,7.78-7.21A5,5,0,0,1,52,29c2.67-.35,5.41-.31,8-.88a49.71,49.71,0,0,0,6.46-2.45c.43-.16,1.17-.32,1.39-.1,2.51,2.48,5,5,7.49,7.66-4.14,4-8.38,8-12.49,12l34.91,35.2c1-2.54,2-4.86,2.73-7.27,3.34-11.54,1.84-22.68-3.1-33.44-5.32-11.56-14-19.94-25.17-25.79-1.14-.6-2.23-1.28-3.46-2,8-2.34,28.89,7.06,37,16.4C123.56,48.83,123.88,70.61,110.52,93.86Z'
					/>
					<polygon
						className='sovietIm soviet-3'
						style={{ opacity: 0 }}
						points='38.81 77.45 14.22 55.13 46.89 49.19 60.51 18.91 76.26 48.14 109.27 51.74 86.34 75.75 93.11 108.25 63.19 93.86 34.37 110.35 38.81 77.45'
					/>
				</svg>

				<Image
					src={stalin}
					alt={'stalin'}
					className={`sovietIm soviet-2 crImage contain`}
				/>

				<Image
					src={lenin}
					alt={'lenin'}
					className={`sovietIm soviet-4 crImage contain`}
				/>
			</div>

			<svg
				className={`another-svg`}
				viewBox='0 0 1464 1015.24'>
				<defs>
					<clipPath id='clip-path'>
						<polygon
							points='540.86 257.71 532.43 258.7 526.41 260.16 518.47 255.69 506.64 239.36 500.69 214.06 496.72 211.09 496.72 205.14 493.75 194.72 495.23 187.78 498.21 187.78 498.71 164.96 500.69 155.04 515.57 141.16 534.42 134.21 549.29 133.22 567.65 140.16 575.58 145.62 581.53 155.54 586.49 160 589.47 176.87 586.99 192.24 592.44 192.74 593.44 199.68 590.96 209.1 580.54 221.01 575.09 219.02 571.12 236.38 540.86 257.71'
							style={{ fill: 'none' }}
						/>
					</clipPath>

					<clipPath id='clip-path-2'>
						<path
							d='M491.94,192.75l-2-18.85,6.45-21.83s6.94-7.44,9.42-9.42,26.3-7.45,26.3-7.45l8.43-2,18.36,3.47,9.92,3.48,7.44,7.93L584.71,160v12.4l2,8.44-2,16.37-1,16.37-1,11.9-5,3-5,20.34-7.94,7.44-9.43,4-24.3-2-17.86-13.89-5-5.46L502.86,228h-5.95L491.45,220l-4-16.87Z'
							style={{ fill: 'none' }}
						/>
					</clipPath>
				</defs>

				<text
					className={`font-neueMetana-regular`}
					transform='translate(0.5 46.51)'
					style={{ fontSize: '51px' }}>
					<tspan style={{ letterSpacing: '0.01999080882352941em' }}>What ma</tspan>

					<tspan
						x='305.38'
						y='0'
						style={{ letterSpacing: '-0.003982843137254902em' }}>
						k
					</tspan>

					<tspan
						x='342.1'
						y='0'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						es
					</tspan>

					<tspan
						x='1000.1'
						y='0'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						so{' '}
					</tspan>

					<tspan
						x='1100.21'
						y='0'
						style={{ stroke: '#000', strokeMiterlimit: 10, letterSpacing: '0.020009957107843136em' }}>
						<tspan
							className={`good-txt`}
							onMouseEnter={() => animateGood()}
							onMouseLeave={() => animateGood(true)}
							style={{ strokeWidth: '3px' }}>
							good
						</tspan>{' '}
						at
					</tspan>

					<tspan style={{ letterSpacing: '0.01999080882352941em' }}>
						<tspan
							x='0'
							y='86.71'>
							what{' '}
						</tspan>

						<tspan
							x='197.26'
							y='86.71'
							style={{ letterSpacing: '0.006012561274509804em' }}>
							w
						</tspan>

						<tspan
							x='252.5'
							y='86.71'>
							e do?{' '}
						</tspan>

						<tspan
							x='459.2'
							y='86.71'
							style={{ letterSpacing: '-0.03100107230392157em' }}>
							W
						</tspan>

						<tspan
							x='516.67'
							y='86.71'
							style={{ letterSpacing: '0.020009957107843136em' }}>
							ell,{' '}
						</tspan>

						<tspan
							x='629.48'
							y='86.71'
							style={{ letterSpacing: '0.005993412990196079em' }}>
							w
						</tspan>

						<tspan
							x='684.72'
							y='86.71'
							style={{ letterSpacing: '0.020009957107843136em' }}>
							e’re a{' '}
						</tspan>

						<tspan
							x='897.64'
							y='86.71'
							style={{ strokeMiterlimit: 10, letterSpacing: '0.020009957107843136em' }}>
							creati
						</tspan>

						<tspan
							x='1099.9'
							y='86.71'
							style={{ strokeMiterlimit: 10, letterSpacing: '0.00603170955882353em' }}>
							v
						</tspan>

						<tspan
							x='1131.88'
							y='86.71'
							style={{ strokeMiterlimit: 10 }}>
							e studio
						</tspan>

						<tspan
							x='1411.71'
							y='86.71'
							xmlSpace='preserve'></tspan>

						<tspan
							x='0'
							y='173.42'>
							from a ti
						</tspan>

						<tspan
							x='292.17'
							y='173.42'
							style={{ letterSpacing: '0.001014859068627451em' }}>
							n
						</tspan>

						<tspan
							x='335.32'
							y='173.42'>
							y{' '}
						</tspan>
					</tspan>
				</text>

				<text
					className={`font-neueMetana-regular`}
					transform='translate(673.96 219.93)'
					style={{ fontSize: '51px' }}>
					<tspan
						xmlSpace='preserve'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						{' ' + ' '}republic, so{' '}
					</tspan>

					<tspan
						x='463.07'
						y='0'
						style={{ letterSpacing: '0.006012561274509804em' }}>
						w
					</tspan>

					<tspan
						x='518.31'
						y='0'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						e kn
					</tspan>

					<tspan
						x='662.53'
						y='0'
						style={{ letterSpacing: '0.013997395833333334em' }}>
						o
					</tspan>

					<tspan
						x='702.98'
						y='0'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						w{' '}
					</tspan>

					<tspan
						x='-673.46'
						y='86.71'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						a thing or t
					</tspan>

					<tspan
						x='-281.12'
						y='86.71'
						style={{ letterSpacing: '0.005993412990196079em' }}>
						w
					</tspan>

					<tspan
						x='-225.89'
						y='86.71'
						xmlSpace='preserve'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						o about{' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' '}the{' '}
					</tspan>

					<tspan
						x='-673.46'
						y='173.42'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						gr
					</tspan>

					<tspan
						x='-597.52'
						y='173.42'
						style={{ letterSpacing: '0.013997395833333334em' }}>
						e
					</tspan>

					<tspan
						x='-556.88'
						y='173.42'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						y, the ugly, and the{' '}
					</tspan>

					<tspan
						x='519.01'
						y='173.42'
						xmlSpace='preserve'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						{' '}
						in
					</tspan>

					<tspan
						x='598.57'
						y='173.42'
						style={{ letterSpacing: '0.013997395833333334em' }}>
						t
					</tspan>

					<tspan
						x='631.82'
						y='173.42'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						o{' '}
					</tspan>

					<tspan
						x='-673.46'
						y='260.13'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						mode
					</tspan>

					<tspan
						x='-483.03'
						y='260.13'
						style={{ letterSpacing: '0.032973345588235295em' }}>
						r
					</tspan>

					<tspan
						x='-451.92'
						y='260.13'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						n,{' '}
					</tspan>

					<tspan
						x='266.25'
						y='260.13'
						style={{ letterSpacing: '-0.03100107230392157em' }}>
						W
					</tspan>

					<tspan
						x='323.73'
						y='260.13'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						e’
					</tspan>

					<tspan
						x='380.65'
						y='260.13'
						style={{ letterSpacing: '0.006012561274509804em' }}>
						v
					</tspan>

					<tspan
						x='412.62'
						y='260.13'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						e also had{' '}
					</tspan>

					<tspan
						x='-673.46'
						y='346.83'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						12{' '}
					</tspan>

					<tspan
						x='-572.89'
						y='346.83'
						style={{ letterSpacing: '0.006012561274509804em' }}>
						y
					</tspan>

					<tspan
						x='-539.13'
						y='346.83'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						ears{' '}
					</tspan>

					<tspan
						x='-366.19'
						y='346.83'
						style={{ letterSpacing: '0.01600796568627451em' }}>
						o
					</tspan>

					<tspan
						x='-325.65'
						y='346.83'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						f{' '}
					</tspan>

					<tspan
						x='-277.65'
						y='346.83'
						style={{ letterSpacing: '0.009018841911764705em' }}>
						e
					</tspan>

					<tspan
						x='-237.26'
						y='346.83'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						xperien
					</tspan>

					<tspan
						x='15.95'
						y='346.83'
						style={{ letterSpacing: '0.0009957107843137254em' }}>
						c
					</tspan>

					<tspan
						x='55.88'
						y='346.83'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						e{' '}
					</tspan>

					<tspan
						x='118.05'
						y='346.83'
						style={{ letterSpacing: '0.013997395833333334em' }}>
						t
					</tspan>

					<tspan
						x='151.3'
						y='346.83'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						o figure out the{' '}
					</tspan>

					<tspan
						x='-673.46'
						y='433.54'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						magic{' '}
					</tspan>

					<tspan
						x='-447.64'
						y='433.54'
						style={{ letterSpacing: '0.009995404411764705em' }}>
						f
					</tspan>

					<tspan
						x='-421.37'
						y='433.54'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						o
					</tspan>

					<tspan
						x='-380.62'
						y='433.54'
						style={{ letterSpacing: '0.032973345588235295em' }}>
						r
					</tspan>

					<tspan
						x='-349.52'
						y='433.54'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						mula{' '}
					</tspan>

					<tspan
						x='-164.49'
						y='433.54'
						style={{ letterSpacing: '0.009995404411764705em' }}>
						f
					</tspan>

					<tspan
						x='-138.22'
						y='433.54'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						or pr
					</tspan>

					<tspan
						x='31.55'
						y='433.54'
						style={{ letterSpacing: '0.013997395833333334em' }}>
						o
					</tspan>

					<tspan
						x='72'
						y='433.54'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						viding the right{' '}
					</tspan>

					<tspan
						onMouseEnter={() => animateExp()}
						onMouseLeave={() => animateExp(true)}
						style={{ textDecoration: 'underline' }}>
						<tspan
							className='ex-txt'
							x='-673.46'
							y='520.25'
							style={{ fontSize: '53px', fontFamily: 'Poppins-SemiBold, Poppins', fontWeight: 600, textDecoration: 'underline' }}>
							digital
						</tspan>

						<tspan
							className='ex-txt-tr'
							x='-508.93'
							y='520.25'
							style={{ fontSize: '53px', fontFamily: 'Poppins-LightItalic, Poppins', fontWeight: 300, fontStyle: 'italic', textDecoration: 'underline' }}>
							{' ' + ' '}experiences
						</tspan>
					</tspan>

					<tspan
						className='ex-txt-tr'
						x='-162.49'
						y='520.25'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						that fit the
					</tspan>

					<tspan
						className='ex-txt-tr'
						x='447.98'
						y='520.25'
						xmlSpace='preserve'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						{' '}
						needs{' '}
					</tspan>

					<tspan
						className='ex-txt-tr'
						x='701.5'
						y='520.25'
						style={{ letterSpacing: '0.01600796568627451em' }}>
						o
					</tspan>

					<tspan
						className='ex-txt-tr'
						x='742.05'
						y='520.25'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						f{' '}
					</tspan>

					<tspan
						x='-673.46'
						y='606.96'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						a
					</tspan>

					<tspan
						x='-631.29'
						y='606.96'
						style={{ letterSpacing: '0.001014859068627451em' }}>
						n
					</tspan>

					<tspan
						x='-588.14'
						y='606.96'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						y cus
					</tspan>

					<tspan
						x='-409.34'
						y='606.96'
						style={{ letterSpacing: '0.01401654411764706em' }}>
						t
					</tspan>

					<tspan
						x='-376.08'
						y='606.96'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						omer, no mat
					</tspan>

					<tspan
						x='81.84'
						y='606.96'
						style={{ letterSpacing: '0.013997395833333334em' }}>
						t
					</tspan>

					<tspan
						x='115.09'
						y='606.96'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						er where th
					</tspan>

					<tspan
						x='519.51'
						y='606.96'
						style={{ letterSpacing: '0.013997395833333334em' }}>
						e
					</tspan>

					<tspan
						x='560.16'
						y='606.96'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						y’re{' '}
					</tspan>

					<tspan
						x='-673.46'
						y='693.67'
						style={{ letterSpacing: '0.01999080882352941em' }}>
						from.{' '}
					</tspan>

					<tspan
						x='-473.34'
						y='693.67'
						style={{ letterSpacing: '-0.03100107230392157em' }}>
						W
					</tspan>

					<tspan
						x='-415.86'
						y='693.67'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						e’re basi
					</tspan>

					<tspan
						x='-124.86'
						y='693.67'
						style={{ letterSpacing: '-0.0049977022058823525em' }}>
						c
					</tspan>

					<tspan
						x='-85.23'
						y='693.67'
						style={{ letterSpacing: '0.020009957107843136em' }}>
						ally{' '}
					</tspan>

					<tspan
						className={`cr-des`}
						x='44.15'
						y='693.67'
						style={{ strokeMiterlimit: 10, letterSpacing: '0.01999080882352941em', strokeWidth: '3px' }}>
						<tspan className='crde crde-1'>c</tspan>

						<tspan className='crde crde-2'>r</tspan>

						<tspan className='crde crde-3'>e</tspan>

						<tspan className='crde crde-4'>a</tspan>

						<tspan className='crde crde-5'>t</tspan>

						<tspan className='crde crde-6'>i</tspan>

						<tspan className='crde crde-7'>v</tspan>

						<tspan className='crde crde-8'>e</tspan>

						<tspan className='crde crde-9'> </tspan>

						<tspan className='crde crde-10'>d</tspan>

						<tspan className='crde crde-11'>e</tspan>

						<tspan className='crde crde-12'>s</tspan>

						<tspan className='crde crde-13'>i</tspan>

						<tspan className='crde crde-14'>g</tspan>

						<tspan className='crde crde-15'>n</tspan>
					</tspan>

					<tspan
						x='-673.46'
						y='780.38'
						style={{ strokeMiterlimit: 10, letterSpacing: '0.01999080882352941em' }}>
						alchemists.
					</tspan>
				</text>

				<path
					d='M454.94,27.41a14.27,14.27,0,0,0,5,11.11A17.84,17.84,0,0,0,472.25,43a18.61,18.61,0,0,0,9.35-2.33,18.92,18.92,0,0,0,6.49-5.91,1.3,1.3,0,0,1,1.18-.67,1.67,1.67,0,0,1,.84.28l.78.45c1.24.71,1.85,1.53,1.85,2.46a3.45,3.45,0,0,1-1,2.19,23.29,23.29,0,0,1-8.4,6.49,25.84,25.84,0,0,1-11.08,2.36q-9.92,0-16.55-5.94a19.19,19.19,0,0,1-6.64-14.95,19.18,19.18,0,0,1,6.64-14.95q6.63-5.94,16.55-5.94a25.83,25.83,0,0,1,11.08,2.35,23.42,23.42,0,0,1,8.4,6.5,3.43,3.43,0,0,1,1,2.18c0,.94-.61,1.76-1.85,2.47l-.78.44a1.58,1.58,0,0,1-.84.28,1.28,1.28,0,0,1-1.18-.67,19.7,19.7,0,0,0-6.55-5.93,18.47,18.47,0,0,0-9.29-2.3,17.85,17.85,0,0,0-12.35,4.42A14.3,14.3,0,0,0,454.94,27.41Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M517.1,48.3A16.07,16.07,0,0,1,505.79,44a13.66,13.66,0,0,1-4.65-10.44A14,14,0,0,1,505.79,23a15.94,15.94,0,0,1,11.31-4.34A15.68,15.68,0,0,1,528.24,23a14,14,0,0,1,4.65,10.59A13.66,13.66,0,0,1,528.24,44,15.83,15.83,0,0,1,517.1,48.3Zm.06-5a9.8,9.8,0,0,0,7.16-2.92,9.2,9.2,0,0,0,3-6.77,9.52,9.52,0,0,0-2.94-7,9.79,9.79,0,0,0-7.19-2.94,10.22,10.22,0,0,0-7.4,2.94,9.41,9.41,0,0,0-3,7,9.11,9.11,0,0,0,3,6.8A10.34,10.34,0,0,0,517.16,43.26Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M546.11,47.51c-1,0-1.46-.46-1.46-1.4V20.74A1.19,1.19,0,0,1,546,19.4h.89a3.1,3.1,0,0,1,2.3.7,3.64,3.64,0,0,1,.78,2.32,17.6,17.6,0,0,1,11.31-3.8c3.89,0,6.86,1.11,8.94,3.33s3.1,5.51,3.1,9.88V46.11c0,.94-.43,1.4-1.28,1.4h-2.58c-1,0-1.46-.46-1.46-1.4V32a8.54,8.54,0,0,0-2.1-6.16,7.94,7.94,0,0,0-6-2.18A13.85,13.85,0,0,0,550,28V46.11a1.24,1.24,0,0,1-1.4,1.4Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M593.37,26.51a9.15,9.15,0,0,0-3,6.83,9.46,9.46,0,0,0,3,6.95,10.2,10.2,0,0,0,7.48,3,12.11,12.11,0,0,0,9.29-4.48,1.77,1.77,0,0,1,1.29-.68,1.56,1.56,0,0,1,.78.23l.45.28c1.23.78,1.85,1.64,1.85,2.57a3.27,3.27,0,0,1-1,2.08,19.23,19.23,0,0,1-2,1.68,19.8,19.8,0,0,1-4.37,2.12,18.19,18.19,0,0,1-6.6,1.24,15.93,15.93,0,0,1-11.32-4.18,13.94,13.94,0,0,1-4.48-10.66,14,14,0,0,1,4.48-10.67,16,16,0,0,1,11.32-4.17,18.18,18.18,0,0,1,6.6,1.23A19.27,19.27,0,0,1,611.57,22a17,17,0,0,1,2,1.68,3.1,3.1,0,0,1,1,2.07c0,.93-.62,1.79-1.85,2.57l-.45.28a1.56,1.56,0,0,1-.78.23,1.78,1.78,0,0,1-1.23-.62q-4.26-4.53-9.35-4.53A10.4,10.4,0,0,0,593.37,26.51Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M654.13,43q-5,5.31-13.89,5.32A20.81,20.81,0,0,1,632.49,47a13.1,13.1,0,0,1-5.27-3.53,14.92,14.92,0,0,1-3.72-9.91A14.19,14.19,0,0,1,628,23q4.49-4.36,11.93-4.36A18.54,18.54,0,0,1,647.08,20a14.35,14.35,0,0,1,5.17,3.44,16.22,16.22,0,0,1,3,4.46,11.42,11.42,0,0,1,1,4.64,3.23,3.23,0,0,1-.53,2,2.5,2.5,0,0,1-2,.64H629.21a9.19,9.19,0,0,0,3.7,5.94,12.35,12.35,0,0,0,7.56,2.24,13,13,0,0,0,10.3-4.93,1.46,1.46,0,0,1,1.12-.62,1.75,1.75,0,0,1,.9.28l.45.28c1.26.79,1.9,1.65,1.9,2.58A2.94,2.94,0,0,1,654.13,43ZM640.3,23.54a13.35,13.35,0,0,0-6.89,1.85,8.4,8.4,0,0,0-4,5.27h21.28A8.7,8.7,0,0,0,647,25.48,11.54,11.54,0,0,0,640.3,23.54Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M669.47,56.75a1.26,1.26,0,0,1-1.45-1.4V20.86a1.29,1.29,0,0,1,1.45-1.46h.68c1.9,0,3,.9,3.13,2.69a13.88,13.88,0,0,1,9.35-3.47A13.46,13.46,0,0,1,692.88,23,14.8,14.8,0,0,1,697,33.51,14.66,14.66,0,0,1,692.91,44a14.49,14.49,0,0,1-19.57.93V55.35c0,.93-.49,1.4-1.46,1.4Zm12.16-33.09a9.51,9.51,0,0,0-4.76,1.23,9.9,9.9,0,0,0-3.53,3.3V38.72A9.35,9.35,0,0,0,676.81,42a9.85,9.85,0,0,0,4.82,1.2,9.66,9.66,0,0,0,9.74-9.74,9.5,9.5,0,0,0-2.83-6.95A9.36,9.36,0,0,0,681.63,23.66Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M705.26,24.38A1.13,1.13,0,0,1,704,23.1V20.8c0-.93.43-1.4,1.29-1.4h5.15V9.66q0-1.29,1.23-1.29h2.69c.93,0,1.4.43,1.4,1.29V19.4h8.06c1,0,1.46.47,1.46,1.4v2.3c0,.86-.49,1.28-1.46,1.28h-8.06v15q0,3.75,3,3.75a5.75,5.75,0,0,0,3.87-1.84,2.17,2.17,0,0,1,1.45-.79,1.32,1.32,0,0,1,.73.28L725,41c1.15.86,1.73,1.7,1.73,2.52a2.94,2.94,0,0,1-1.12,2,10.25,10.25,0,0,1-7.39,2.8,7.49,7.49,0,0,1-5.77-2.24c-1.38-1.5-2.07-3.66-2.07-6.5V24.38Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M763.55,36.54q6.11,6.43,16,6.44A22.92,22.92,0,0,0,789.34,41q4.13-1.92,4.12-5.46a3.8,3.8,0,0,0-.7-2.29,5.58,5.58,0,0,0-1.71-1.54,11,11,0,0,0-3-1,37.06,37.06,0,0,0-3.67-.64q-1.65-.2-4.62-.48c-1.76-.15-3.22-.31-4.4-.47s-2.58-.41-4.23-.73A18.06,18.06,0,0,1,767,27.16a20.43,20.43,0,0,1-3.17-1.85,6.92,6.92,0,0,1-2.32-2.75,8.6,8.6,0,0,1-.79-3.72q0-5.37,5-8.85T779.4,6.52a33.51,33.51,0,0,1,7.17.73,23.33,23.33,0,0,1,5.54,1.82,31.78,31.78,0,0,1,3.28,1.82,13.4,13.4,0,0,1,1.71,1.29,2.72,2.72,0,0,1,1,2,3.27,3.27,0,0,1-1.85,2.63l-.79.5a1.56,1.56,0,0,1-.78.23,1.78,1.78,0,0,1-1.23-.62,17.41,17.41,0,0,0-5.66-3.5,22.07,22.07,0,0,0-8.57-1.54,20.29,20.29,0,0,0-8.9,1.79c-2.46,1.2-3.7,2.8-3.7,4.82a3.64,3.64,0,0,0,1,2.63,7.29,7.29,0,0,0,3.19,1.71,33.48,33.48,0,0,0,4.34,1c1.46.22,3.31.43,5.55.61q10,.84,14.3,3.17A7.83,7.83,0,0,1,799.34,35a10.66,10.66,0,0,1-2.77,7.39,16.14,16.14,0,0,1-7.2,4.51,32.22,32.22,0,0,1-9.91,1.43,34.59,34.59,0,0,1-8-.87,23,23,0,0,1-6-2.21A40.59,40.59,0,0,1,761.9,43,15.16,15.16,0,0,1,760,41.3a3.06,3.06,0,0,1-1-2c0-.93.62-1.79,1.85-2.58l.78-.5a1.58,1.58,0,0,1,.84-.28A1.71,1.71,0,0,1,763.55,36.54Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M807.91,24.38a1.13,1.13,0,0,1-1.29-1.28V20.8c0-.93.43-1.4,1.29-1.4h5.15V9.66q0-1.29,1.23-1.29H817c.93,0,1.4.43,1.4,1.29V19.4h8.06c1,0,1.46.47,1.46,1.4v2.3c0,.86-.49,1.28-1.46,1.28h-8.06v15q0,3.75,3,3.75a5.75,5.75,0,0,0,3.87-1.84,2.15,2.15,0,0,1,1.45-.79,1.32,1.32,0,0,1,.73.28l.22.17q1.74,1.29,1.74,2.52a2.94,2.94,0,0,1-1.12,2,10.26,10.26,0,0,1-7.39,2.8,7.47,7.47,0,0,1-5.77-2.24c-1.38-1.5-2.07-3.66-2.07-6.5V24.38Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M867.1,19.4a1.26,1.26,0,0,1,1.45,1.4V46.17a1.18,1.18,0,0,1-1.34,1.34h-.9a3.17,3.17,0,0,1-2.35-.7,3.56,3.56,0,0,1-.73-2.38,17.68,17.68,0,0,1-11.42,3.87q-5.77,0-8.9-3.39t-3.14-9.83V20.8c0-.93.43-1.4,1.29-1.4h2.57c1,0,1.46.47,1.46,1.4V34.91a8.52,8.52,0,0,0,2.1,6.13,7.9,7.9,0,0,0,6,2.22,14.06,14.06,0,0,0,10-4.43v-18a1.24,1.24,0,0,1,1.4-1.4Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M907.75,47.51h-.5c-1.94,0-3-.86-3.3-2.57a14.06,14.06,0,0,1-9.36,3.36A13.49,13.49,0,0,1,884.37,44a14.7,14.7,0,0,1-4.06-10.5A14.84,14.84,0,0,1,884.4,23a13.47,13.47,0,0,1,10.25-4.34A13.73,13.73,0,0,1,903.89,22V5.57a1.29,1.29,0,0,1,1.46-1.46h2.4a1.29,1.29,0,0,1,1.46,1.46V46.11C909.21,47.05,908.72,47.51,907.75,47.51ZM895.66,43.2a9.92,9.92,0,0,0,8.23-4.37V28.08a10,10,0,0,0-3.53-3.22,9.58,9.58,0,0,0-4.7-1.2,9.38,9.38,0,0,0-6.92,2.85,9.54,9.54,0,0,0-2.83,7,9.66,9.66,0,0,0,9.75,9.74Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M929,12.51h-5a1.19,1.19,0,0,1-1.35-1.34V8.65a1.19,1.19,0,0,1,1.35-1.35h5a1.19,1.19,0,0,1,1.34,1.35v2.52A1.19,1.19,0,0,1,929,12.51Zm-3.92,35c-.86,0-1.29-.46-1.29-1.4V20.8c0-.93.43-1.4,1.29-1.4h2.57c1,0,1.46.47,1.46,1.4V46.11c0,.94-.49,1.4-1.46,1.4Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<path
					d='M956.81,48.3A16.06,16.06,0,0,1,945.5,44a13.66,13.66,0,0,1-4.65-10.44A14,14,0,0,1,945.5,23a15.92,15.92,0,0,1,11.31-4.34A15.7,15.7,0,0,1,968,23a14,14,0,0,1,4.65,10.59A13.66,13.66,0,0,1,968,44,15.85,15.85,0,0,1,956.81,48.3Zm.05-5A9.83,9.83,0,0,0,964,40.34a9.2,9.2,0,0,0,3-6.77,9.52,9.52,0,0,0-2.94-7,9.79,9.79,0,0,0-7.2-2.94,10.21,10.21,0,0,0-7.39,2.94,9.41,9.41,0,0,0-3,7,9.11,9.11,0,0,0,3,6.8A10.33,10.33,0,0,0,956.86,43.26Z'
					style={{ strokeMiterlimit: 10, strokeWidth: '0.25px' }}
				/>

				<rect
					className={`creative-layer`}
					onMouseEnter={() => animateCreative()}
					onMouseLeave={() => animateCreative(true)}
					style={{ opacity: 0 }}
					x='703.71'
					y='870.3'
					width='540.99'
					height='62.36'></rect>

				<path
					className='ex-txt-trp'
					d='M936.63,712.9a.89.89,0,0,1,.59.6v18q.38,8.6,4.74,9c2,0,3.45-1.66,4.44-4.81a17.13,17.13,0,0,0,.74-5v-4.07q0-6.43-3-7.4l-.51-.15c-.4-.15-.62-.34-.67-.59a.72.72,0,0,1,.59-.52,21.36,21.36,0,0,0,12.73-4.74,1.65,1.65,0,0,0,.3-.22.69.69,0,0,1,.37-.15.74.74,0,0,1,.59.6v18.87a18.51,18.51,0,0,0,.44,4.44c.45,1.38,1.14,2.17,2.07,2.36a1.78,1.78,0,0,0,.74,0h.45c.39,0,.56.2.52.6a1,1,0,0,1-.23.44,13.28,13.28,0,0,1-6.43,1.78c-3.51,0-5.92-1.16-7.26-3.33q-2.65,3.4-9,3.33c-5.14,0-8.66-1.78-10.59-5.18a11.35,11.35,0,0,1-1.33-5.26v-4.88q0-6.43-3-7.4l-.52-.15c-.39-.15-.62-.34-.67-.59a.72.72,0,0,1,.6-.52A21.33,21.33,0,0,0,936,713.27a1.43,1.43,0,0,1,.3-.22A.72.72,0,0,1,936.63,712.9Zm41.51-.07a.82.82,0,0,1,.3.07.72.72,0,0,1,.22.52v2.74A9.19,9.19,0,0,1,986,713q10.72.07,12.43,10.36l1.18,10.43c.45,3.51,1.56,5.6,3.33,6.29a13.37,13.37,0,0,0,1.34.37c.39.1.61.27.66.52a.63.63,0,0,1-.59.59H986.43c-.39-.1-.62-.29-.66-.59a.74.74,0,0,1,.66-.52,3.07,3.07,0,0,0,3-2.88,14.35,14.35,0,0,0-.07-3.78l-1.19-11.47q-1-7.47-5-7.4a3.22,3.22,0,0,1-.59.08c-2,.24-3.16,1.82-3.55,4.73a41,41,0,0,0-.3,5.7v8.36c0,3.51.86,5.6,2.59,6.29a9.71,9.71,
				0,0,0,1.26.37c.34.1.54.27.59.52-.1.35-.27.54-.52.59H964.53a.71.71,0,0,1-.59-.59.69.69,0,0,1,.59-.52,3.75,3.75,0,0,0,3.55-3.1,16.9,16.9,0,0,0,.3-3.56v-7.1c0-3.36-.47-5.5-1.41-6.44a5.26,5.26,0,0,0-2.22-1.18c-.4-.2-.59-.4-.59-.59a.73.73,0,0,1,.59-.6,20.82,20.82,0,0,0,12.65-4.66,3.5,3.5,0,0,1,.37-.22A.72.72,0,0,1,978.14,712.83Zm42.85.22c.34-.29.62-.27.81.08a1.18,1.18,0,0,1,.08.37v20.27c0,3.51.86,5.6,2.59,6.29a8.87,8.87,0,0,0,1.26.37c.34.1.54.27.59.52s-.27.54-.52.59h-18.06a.73.73,0,0,1-.59-.59.72.72,0,0,1,.59-.52,3.73,3.73,0,0,0,3.55-3.1,16.9,16.9,0,0,0,.3-3.56v-7.1c0-4.29-1-6.76-3.11-7.4l-.51-.15c-.4-.15-.6-.34-.6-.59a.62.62,0,0,1,.6-.52,21.27,21.27,0,0,0,12.65-4.74Zm-5.11-15.91a5.8,5.8,0,0,1,5,2.59,6,6,0,0,1,1,3.41,5.75,5.75,0,0,1-3,5.25,7.63,7.63,0,0,1-3,.82,5.82,5.82,0,0,1-4.73-2.45,5.76,5.76,0,0,1-1.26-3.62,5.69,5.69,0,0,1,3-5.18A6.16,6.16,0,0,1,1015.88,697.14Zm40,15.91a.5.5,0,0,1,.67-.07.7.7,0,0,1,.22.52v36c0,3.5.86,5.59,2.59,6.29a8.87,8.87,0,0,0,1.26.36.74.74,0,0,1,.59.52c-.1.35-.27.55-.52.6H1042.6a.74.74,0,0,1-.6-.6.73.73,0,0,1,.6-.52,3.75,3.75,0,0,0,3.55-3.1,16.8,16.8,0,0,0,.3-3.55V739.47a13,13,0,0,1-6.74,2.59h-1.33a12.34,
				12.34,0,0,1-9.84-5.62,15.84,15.84,0,0,1-2.82-8.14,14.5,14.5,0,0,1,4.22-11.7,12.27,12.27,0,0,1,8-3.62,2.73,2.73,0,0,1,.67-.08,12.5,12.5,0,0,1,9.32,4.22A24.26,24.26,0,0,0,1055.84,713.05Zm-18.13,1a2.46,2.46,0,0,0-2,1.26q-2.14,3.61.3,12.73t6,11.32a.21.21,0,0,0,.15.07q2.07,1.11,3.41-1.11l.15-.15q1.77-3.76-.6-12.5-2.29-8.22-5.4-10.81A3.29,3.29,0,0,0,1037.71,714.09Zm34.41-1.19a.87.87,0,0,1,.59.6v18q.38,8.6,4.74,9c2,0,3.45-1.66,4.44-4.81a17.13,17.13,0,0,0,.74-5v-4.07q0-6.43-3-7.4l-.52-.15c-.4-.15-.62-.34-.67-.59a.72.72,0,0,1,.59-.52,21.36,21.36,0,0,0,12.73-4.74,1.65,1.65,0,0,0,.3-.22.72.72,0,0,1,.37-.15.74.74,0,0,1,.59.6v18.87a18.51,18.51,0,0,0,.44,4.44c.45,1.38,1.14,2.17,2.08,2.36a1.76,1.76,0,0,0,.74,0h.44c.39,0,.57.2.52.6a1.07,1.07,0,0,1-.22.44,13.35,13.35,0,0,1-6.44,1.78c-3.51,0-5.92-1.16-7.25-3.33q-2.67,3.4-9,3.33-7.69-.07-10.58-5.18a11.36,11.36,0,0,1-1.34-5.26v-4.88q0-6.43-3-7.4l-.52-.15c-.39-.15-.61-.34-.66-.59a.7.7,0,0,1,.59-.52,21.36,21.36,0,0,0,12.73-4.74,1.17,1.17,0,0,1,.29-.22A.72.72,0,0,1,1072.12,712.9Zm40,0a13.17,13.17,0,0,1,9.62,3.56,12.08,12.08,0,0,1,.88,1,6.41,6.41,0,0,1,1.34,6.07q-1.11,3-5,3.55-2.3.3-6.73.81h-.3l-.07.08h-.59l-.08.07h-.51l-.08.08H1110l-.07.07h-.52l-.08.07h-.51l-.08.08h-.59q1.26,5.91,6.14,8a8.08,8.08,0,0,0,8.07-.59,8.36,8.36,0,0,0,2.37-2.52q.73-1,1.11-1a1,1,0,0,1,.29.66q-2.6,9.11-13.84,9.11a14.87,14.87,0,0,1-11.24-4.66,13.76,13.76,0,0,1-3.11-5.56,11.53,11.53,0,0,1-.59-2.81q-.83-9.24,5.18-13.46A16.6,16.6,0,0,1,1112.16,712.9Zm-.08,1.12q-3,0-4.14,5a16.85,16.85,0,0,0,0,8.29q8.58-1,8.43-6.44v-.66c-.34-3.56-1.43-5.55-3.25-6A3.72,3.72,0,0,0,1112.08,714Z'
				/>

				<path
					className={`transform-g transform-anim`}
					transform='rotate(0 0 0)'
					d='M1186.34,267.84v-5.12h-5.12v2.57H968.67v-2.06h-5.12v2.06H743.22v-2.57H738.1v5.12h2.06v49.78h-1.54v5.12h5.11v-2.57H963.55v1.55h5.12v-1.55h192.26a32.78,32.78,0,0,0,4.5.33,22.45,22.45,0,0,0,3.79-.33h12v2.57h5.12v-5.12h-2.57V267.84Zm-445.18,0h2.06v-1.55h10.27l-4.06,1.38,0,11.37h-8.22Zm423.91,48.94c-6.37,0-14.28-3-17.9-5.15l-1.48,3.62a43.82,43.82,0,0,0,10.25,3.92H968.67V316.6h-5.12v2.57H743.73v-1.55h-2.57V282.41h8.11v12.34c0,6.68,3.27,11.32,10.2,11.32h10.66v-3.36H761c-5.15,0-6.63-3-6.63-7.76V282.41h11.89l1.17-3.37H754.48l0-12.75h209v2.06h5.12v-2.06h212.55v1.55h1.55v10.29h-4.9v7.95c-2.75-4.08-9.08-8.06-16-8.06-10.61,0-17.44,6.33-17.44,14.33,0,7.09,6.17,13.47,16.47,13.47,6.94,0,14.38-4.34,17-8.47v7.75C1177.87,311.53,1173.49,316.78,1165.07,316.78Zm12.39-25.09c0,4.44-7.85,10.91-15.5,10.91-8.16,0-12.65-4.48-12.65-10.25,0-6,4.69-11.32,13.47-11.32C1171.34,281,1177.46,287.3,1177.46,291.69Zm3.76,25.93v1.55h-8.36c5.27-2,9.13-6.16,9.91-11.69v10.14Zm-352.8-38.68H809.3l1.43,3.72h15.15c2.54,0,6.27,1.79,6.27,7.76,0,.56,0,2.55,0,4.53-4.39-3-10.87-5.91-16.88-5.91-5.82,0-13.16,2.75-13.16,8.82s7.24,8.52,12.49,8.52c5.72,0,13.37-3,17.55-5.92v5.61h5.1V290.31C837.2,283.17,833.12,279.6,828.42,278.94ZM832.1,298c-3.06,2-9.69,5.82-15.76,5.82-3.88,0-9.18-1.58-9.18-6s5.15-6.12,9.48-6.12c7,0,13.82,4.69,15.46,5.86Zm89.35.05c-.05,6.28-9.28,8.52-17,8.52-11.58,0-17-3.26-17-8.77h5.26c.1,5.2,6.52,5.51,11.62,5.51,7.5,0,12.29-1.89,12.29-5.26,0-8.26-29.37.57-29.37-11.62,0-6.43,8.72-8.52,17.59-8.52,8.21,0,16,1.33,16,9h-5c0-5.15-5-5.66-11-5.66-7.49,
				0-12.24,1.42-12.24,5.45C892.58,294.75,921.45,286.28,921.45,298.06ZM796.35,278a12.18,12.18,0,0,1,4.79,1.07v3.47a11.89,11.89,0,0,0-6.53-.76c-8.41.4-12.75,8.41-12.75,10.86v13.46h-5.1V277.41h5.1v9.08A16.35,16.35,0,0,1,796.35,278Zm85,13.16v14.94h-5V291.13c0-4.18-3.77-9.38-10.65-9.38-7,0-12.24,5.2-14.08,10.6v13.72h-5.1V278.28h5.1v8.87c3.47-4.79,8.67-8.36,14.94-8.36C874.12,278.79,881.31,283.28,881.31,291.13Zm89.25-13.21c-9.23,0-18.15,5.61-18.15,14.54s8.77,14.43,18,14.43,17.85-5.46,17.85-14.43S979.69,277.92,970.56,277.92Zm-.2,26.06c-7.19,0-12.65-5.15-12.65-11.52,0-6.07,5.15-11.48,12.65-11.48S983,286.08,983,292.46,977.5,304,970.36,304Zm117.81-36.87h5.15v8.47h-5.15Zm49.56,24v14.94h-5V291.13c0-4.18-3.78-9.38-10.66-9.38-7,0-12.24,5.2-14.08,10.6v13.72h-5.1V278.28h5.1v8.87c3.47-4.79,8.67-8.36,15-8.36C1130.54,278.79,1137.73,283.28,1137.73,291.13Zm-49.56-10.56h5.15v25.5h-5.15Zm-8.78,9.28v16.22h-5V289.85c0-4.84-2.7-8.31-8-8.31-6.22,0-10,5.46-10.76,11v13.51h-5V291.23c0-6.37-2.7-9.69-8.06-9.69a10.7,10.7,0,0,0-10.66,9.84v14.69h-5.1V278.28h5.1v8c2.4-4.69,6.58-7.7,12-7.7,6.17,0,9.94,4,10.91,9.49,2.5-5.35,6.94-9.49,12.9-9.49C1074.34,278.58,1079.39,283.28,1079.39,289.85ZM1015.34,278a12.18,12.18,0,0,1,4.79,1.07v3.47a11.89,11.89,0,0,0-6.53-.76c-8.41.4-12.74,8.41-12.74,10.86v13.46h-5.1V277.41h5.1v9.08A16.33,16.33,0,0,1,1015.34,278Zm-72.78-10.81h9.18v4.23h-7.65c-4.59,0-6.42,2.25-6.42,6.38v5.05h11.67l-.66,3.11h-11v20.14h-5.1V285.93h-7.86v-3.11h7.86v-5.71C932.57,271.09,936.14,267.16,942.56,267.16Z'
				/>

				<rect
					onMouseEnter={() => animateTransform()}
					onMouseLeave={() => animateTransform(true)}
					x='737.71'
					y='263.3'
					width='450.99'
					height='62.36'
					style={{ opacity: 0 }}
				/>

				<g className={`soviet-g`}>
					<path d='M396.89,221.18V167.53h27.77v15.11h-9.55v-4.09h-8.67v10.29h11.39v11H406.44v10.3h8.67v-4.09h9.55v15.11Zm63.52-18.4v18.4h-9.49v-14l-4.43-5-4.36,5v14h-9.49v-18.4l7.21-8.24-7.21-8.33V167.53h9.49v14.3l4.36,5,4.43-5v-14.3h9.49v18.68l-7.21,8.33Zm25.69-3.28H468.39v-11H486.1Zm8-32h27.77V184h-9.49v-5.47h-8.79v3.5l18.28,21.1v18H494.08v-16.5h9.49v5.48h8.79v-2.85l-18.28-20.95Zm35.75,0H557.6v53.65H529.83Zm18.28,42.63V178.55h-8.79v31.61Zm45.31-42.63v53.58H580.19l-14.61-16.57v-37h9.55v32.55l8.73,10.08V167.53Zm27.84,11h-5.7v31.61h5.7v11H600.38v-11h5.69V178.55h-5.69v-11h20.88Zm7.15,42.63V167.53h27.77v15.11h-9.55v-4.09H638v10.29h11.39v11H638v10.3h8.67v-4.09h9.55v15.11Zm35-53.65h35.37V188.7h-9.49V178.55h-3.42v42.63h-9.55V178.55H673V188.7H663.4Z' />

					<rect
						onMouseMove={(e) => animateSoviet(e)}
						onMouseLeave={(e) => animateSoviet(e, true)}
						x='380.71'
						y='163.3'
						width='330.99'
						height='62.36'
						style={{ opacity: 0 }}
					/>
				</g>

				<g
					className={`depress-g`}
					onMouseEnter={() => animateDepress()}
					onMouseLeave={() => animateDepress(true)}
					style={{ strokeWidth: '3px' }}>
					<text
						className={`depress depress1 font-neueMetana-regular`}
						transform='translate(806.92 392.82)'
						style={{ fontSize: '51px', strokeMiterlimit: 10, letterSpacing: '0.020000382965686275em' }}>
						depressing
					</text>
					<text
						className={`depress depress2 font-neueMetana-regular`}
						transform='translate(806.92 392.82)'
						style={{ fontSize: '51px', strokeMiterlimit: 10, letterSpacing: '0.020000382965686275em' }}>
						depressing
					</text>
					<text
						className={`depress depress3 font-neueMetana-regular`}
						transform='translate(806.92 392.82)'
						style={{ fontSize: '51px', strokeMiterlimit: 10, letterSpacing: '0.020000382965686275em' }}>
						depressing
					</text>
					<text
						className={`depress depress4 font-neueMetana-regular`}
						transform='translate(806.92 392.82)'
						style={{ fontSize: '51px', strokeMiterlimit: 10, letterSpacing: '0.020000382965686275em' }}>
						depressing
					</text>
				</g>

				<g
					className={`lineg`}
					style={{ pointerEvents: 'all' }}
					onMouseEnter={() => animateLine()}
					onMouseLeave={() => animateLine(true)}>
					<path
						className={`lineMove lineMove`}
						d='M179.63,356.64c11.12,1.9,25.49,5.13,40.8,8.8,7.65,1.83,15.52,3.77,23.35,5.71,21,5.2,41.52,10.39,56.19,13.46,10.38,2.17,110-4.58,86.92-20.78S359.49,331.22,329,347s-79.29,59-86.79,51.42S394.3,411,403,399.28c6.09-8.18,20.69-21.51,18.86-30-.79-3.66-4.59-6.44-13.38-7.56-29.29-3.73-175.19,35.89-179.55,25.51s26.61-23.22,81.23-15.37c36.33,5.21,109.69,2.46,147.48,5.24,19,1.4,29,4.2,20.78,10.13-24.66,17.7-55.72,38.33-83,21.53a47.65,47.65,0,0,1-16.17-16.6c-8.72-14.27-11-28.47-38.65-28.28-38.94.26-107.33-5.62-118.94,13.94s-33.09,40.11,24.66,30.94S488.56,371,471.18,387.18s55.15-9.31,10.17-25.51C443,347.84,355.74,402.78,310.49,409.89c-7.79,1.23-14.34,1-19.17-1.18-32.92-15-144.59-45.32-111.69-30.94,0,0,149.4-10.08,111.2-29.54s-43,35.28-59,53.65-32.88,33.49-44.49,7.2-22.72-65.9,33.37-63.74c38.58,1.49,72.92,34.74,101.38,53.42,12.92,8.48,24.62,14,35,12.12,33.15-5.89,37.5-63.78,67.53-56.66s104.12,38.3,75.59,48.38-2.42,15.12-59.47-2.88-44-64.44-76.88-49,1,45.73-33.36,41.41-34.81-45.38-1.45-36.38,64.79,49.7,46.9,53.3-122.33-56.54-98.15-62.3c0,0,54.55-12.4,111.69,24s82.13-13.58,82.13-13.58-16-22-33-5,28.16,35.57,20.08,48.28-43.08,22.72-44.08-1.28S296,391.88,264.29,397.05s-110.13-14.74-75.91-38.78,44.47,19.46,66.85,37.21,76.37,23.74,105.37,4.74,151-10,136-7-80,43-81,0,12-57-26-45-130,49-141,30-22-45-35-38'
						style={{ fill: 'none', strokeMiterlimit: 10, strokeWidth: '1.98425197601318px' }}
					/>
				</g>

				<g>
					<path
						className={`star star1`}
						d='M548.22,499.13a7,7,0,0,1-5.48,5.23.11.11,0,0,0,0,.22,8.34,8.34,0,0,1,5.47,5.53.11.11,0,0,0,.22,0c.37-1.21,1.77-4.87,5.4-5.54a.11.11,0,0,0,0-.22,6.54,6.54,0,0,1-5.39-5.22A.12.12,0,0,0,548.22,499.13Z'
					/>

					<path
						className={`star star2`}
						d='M388.22,429.13a7,7,0,0,1-5.48,5.23.11.11,0,0,0,0,.22,8.34,8.34,0,0,1,5.47,5.53.11.11,0,0,0,.22,0c.37-1.21,1.77-4.87,5.4-5.54a.11.11,0,0,0,0-.22,6.54,6.54,0,0,1-5.39-5.22A.12.12,0,0,0,388.22,429.13Z'
					/>

					<path
						className={`star star3`}
						d='M792.22,426.13a7,7,0,0,1-5.48,5.23.11.11,0,0,0,0,.22,8.34,8.34,0,0,1,5.47,5.53.11.11,0,0,0,.22,0c.37-1.21,1.77-4.87,5.4-5.54a.11.11,0,0,0,0-.22,6.54,6.54,0,0,1-5.39-5.22A.12.12,0,0,0,792.22,426.13Z'
					/>

					<path d='M309.84,466.88c0,3.35.73,11.41,5.54,11.41,6.4,0,9.07-3.54,10.58-6.65h1.17c-1.66,3.26-4.33,7.43-11.75,7.43-8.79,0-15.25-3.45-15.25-12.28v-2.14c0-9.42,7.57-12.28,16.51-12.28,5.14,0,8.88,2.67,8.88,6-.34,2.57-2,4.76-4.8,4.76a4.62,4.62,0,0,1-.34-9.23,9.41,9.41,0,0,0-3.84-.78c-6.31,0-6.7,8.21-6.7,11.41Zm19.47-26.65v-.78l10.87-2.67v20a13,13,0,0,1,9.72-4.62c6.5,0,9.71,4.86,9.71,11.51V478h1.16v.78h-12V478h1.17V463.53c0-5.68-2.29-7.33-5.3-7.33a6.52,6.52,0,0,0-4.42,1.51V478h1.17v.78h-12V478h1.16V439.94Zm35.35,24.42c0-8.3,6.16-12.28,13.83-12.28s13.84,4,13.84,12.28v1.07h-18v1.16c0,7.77.58,11.41,5.53,11.41,6.41,0,9.08-3.54,10.59-6.65h1.16c-1.65,3.26-4.32,7.43-11.75,7.43-9.08,0-15.24-4-15.24-12.28Zm9.71.29h8.25v-.39c0-7.76-.58-11.41-4.13-11.41s-4.12,3.65-4.12,11.41Zm23.06-.29c0-8.3,6.16-12.28,13.83-12.28s13.84,4,13.84,12.28v1.07h-18v1.16c0,7.77.58,11.41,5.53,11.41,6.41,0,9.08-3.54,10.59-6.65h1.16c-1.65,3.26-4.32,7.43-11.75,7.43-9.08,0-15.24-4-15.24-12.28Zm9.71.29h8.25v-.39c0-7.76-.58-11.41-4.13-11.41s-4.12,3.65-4.12,11.41Zm22.57,13h1.17v-23.5l-1.17.3V454l10.88-2.67v8.5c1.36-2.67,5.24-7.72,9.61-7.72,3.64,0,5.59,3.15,5.68,6v.19a4.61,4.61,0,1,1-4.61-4.61,7,7,0,0,1,1,.1,3,3,0,0,0-2-.59c-4.27,0-9.13,7-9.71,10.64V478h1.16v.78h-12Zm29.86-25.34v-3.79c0-9.9,7.53-12.43,14.76-12.43,4.86,0,7.24,2.72,7.63,5.49a2.91,2.91,0,0,1,.09.58v.24a4.61,4.61,0,1,1-4.61-4.61,7,7,0,0,1,1,.1,7.5,7.5,0,0,0-4.13-1c-2.28,0-5.05,1.7-5.05,8.64v6.8h5.39v1.6h-5.39V478h1.17v.78h-12V478h1.16V454.21h-3.06v-1.6Zm19,2.14V454l10.87-2.67v15.24c0,6.51.83,7.34,2.53,7.34a6.82,6.82,0,0,0,3.49-1.46v-18l-1.16.3V454l10.87-2.67V478h1.17v.78H495.5v-4.85a14.66,14.66,0,0,1-9.27,4.07c-3.21,0-6.46-1.45-6.46-11.41V454.45Zm32.62-15.35,10.88-2.67V478h1.16v.78h-12V478h1.17V439.89l-1.17.29Zm29.33,34c0-7.28,11.31-10.05,16-10.05.78,0,1.46.05,2,.1v-3.89c0-4.85-.78-6.7-4.18-6.7a18.07,18.07,0,0,0-5.68,1,4.61,4.61,0,1,1-5.48,4.52c0-4.95,7.47-6.31,11.16-6.31,7.53,0,13.89,1.84,13.89,11V478h1.17v.78H558.62v-4a13.75,13.75,0,0,1-10,4.32C542.94,479.07,540.56,476.4,540.56,473.39Zm12.77,1.75a11.39,11.39,0,0,0,5.29-1.41v-9.52c-.44,0-1-.09-1.46-.09-4.66.24-6.6,3.2-6.89,6.94C550.27,473.63,550.8,475.14,553.33,475.14Zm21-20.39V454l10.88-2.67v5.39a12.93,12.93,0,0,1,9.71-4.61c6.5,0,9.71,4.85,9.71,11.5V478h1.16v.78h-12V478h1.17V463.49c0-5.69-2.29-7.34-5.3-7.34a6.49,6.49,0,0,0-4.41,1.51V478h1.16v.78h-12V478h1.16v-23.5Zm51.76-2.38c.63,0,1.21.05,1.84.09V439.89l-1.16.29v-.78l10.87-2.67V478h1.17v.78H628V474.8a11.39,11.39,0,0,1-8.54,4.27c-5,0-9.52-4.42-9.52-12v-.58C609.89,458.53,617.85,452.37,626.11,452.37Zm-6.51,14v.78c0,5.63,1.89,8,4.61,8a7,7,0,0,0,3.74-1.36V453.24a13.38,13.38,0,0,0-1.45-.1C623,453.14,619.6,458.58,619.6,466.35Zm37.68-1.7c0-8.3,6.16-12.28,13.83-12.28s13.84,4,13.84,12.28v1.07H667v1.16c0,7.77.58,11.41,5.53,11.41,6.41,0,9.08-3.54,10.59-6.65h1.16c-1.65,3.26-4.32,7.43-11.75,7.43-9.08,0-15.24-4-15.24-12.28Zm9.71.29h8.25v-.39c0-7.76-.58-11.41-4.13-11.41S667,456.79,667,464.55Zm22.57-10.19V454l10.88-2.67v5.39a13,13,0,0,1,9.71-4.61c6.51,0,9.71,4.85,9.71,11.5V478H721v.78H709V478h1.17V463.49c0-5.69-2.28-7.34-5.29-7.34a6.5,6.5,0,0,0-4.42,1.51V478h1.16v.78h-12V478h1.17v-23.5ZM739.23,493v.77c-7.82,0-14.32-2.76-14.32-7.91,0-4.52,6.16-6.17,9.47-6.17a4.53,4.53,0,0,1,.87.05v.63c-1.17.49-3,2.19-3,5.49C732.29,489.75,734.76,493,739.23,493Zm8-7.14c0,3.84-2.62,7.14-7.67,7.14v.77c9.66,0,15.59-4.71,15.59-10,0-13.54-26.22-8-26.22-14a3.5,3.5,0,0,1,.39-1.41c2.57,1.9,6.41,2.87,10.58,2.87,7.43,0,13.79-3,13.79-9v-1c0-3.78-2.62-6.41-6.46-7.76a7.67,7.67,0,0,1-.58-1.66,4,4,0,0,0,2.38.78,3.86,3.86,0,0,0,3.84-3.88,3.86,3.86,0,0,0-7.72,0,13.05,13.05,0,0,0,.58,4.32,22.39,22.39,0,0,0-5.83-.73c-7.38,0-13.69,3-13.69,8.93v1a7.29,7.29,0,0,0,1.89,5,6.5,6.5,0,0,0-1.45,3.78C726.61,481.3,747.19,477.81,747.19,485.82Zm-4.12-22.63c0,4.71-.88,7.19-3.21,7.19s-3.15-2.48-3.15-7.19v-3c0-4.71.92-7.18,3.15-7.18s3.21,2.47,3.21,7.18Zm13.35,10.2c0-7.28,11.31-10.05,16-10.05.78,0,1.46.05,2,.1v-3.89c0-4.85-.78-6.7-4.18-6.7a18.07,18.07,0,0,0-5.68,1,4.61,4.61,0,1,1-5.48,4.52c0-4.95,7.47-6.31,11.16-6.31,7.53,0,13.89,1.84,13.89,11V478h1.17v.78H774.48v-4a13.75,13.75,0,0,1-10.05,4.32C758.8,479.07,756.42,476.4,756.42,473.39Zm12.77,1.75a11.39,11.39,0,0,0,5.29-1.41v-9.52c-.44,0-1-.09-1.46-.09-4.66.24-6.6,3.2-6.89,6.94C766.13,473.63,766.66,475.14,769.19,475.14ZM803,493v.77c-7.82,0-14.32-2.76-14.32-7.91,0-4.52,6.16-6.17,9.47-6.17a4.58,4.58,0,0,1,.87.05v.63c-1.17.49-3,2.19-3,5.49C796,489.75,798.51,493,803,493Zm8-7.14c0,3.84-2.62,7.14-7.67,7.14v.77c9.66,0,15.59-4.71,15.59-10,0-13.54-26.22-8-26.22-14a3.35,3.35,0,0,1,.39-1.41c2.57,1.9,6.41,2.87,10.58,2.87,7.43,0,13.79-3,13.79-9v-1c0-3.78-2.62-6.41-6.46-7.76a7.67,7.67,0,0,1-.58-1.66,4,4,0,0,0,2.38.78,3.86,3.86,0,0,0,3.84-3.88,3.86,3.86,0,0,0-7.72,0,13.05,13.05,0,0,0,.58,4.32,22.39,22.39,0,0,0-5.83-.73c-7.38,0-13.69,3-13.69,8.93v1a7.29,7.29,0,0,0,1.89,5,6.5,6.5,0,0,0-1.45,3.78C790.36,481.3,810.94,477.81,810.94,485.82Zm-4.12-22.63c0,4.71-.88,7.19-3.21,7.19s-3.15-2.48-3.15-7.19v-3c0-4.71.92-7.18,3.15-7.18s3.21,2.47,3.21,7.18Zm26.36-11.89V478h1.16v.78h-12V478h1.17v-23.5l-1.17.3V454Zm-9.86-7.77a5,5,0,1,1,5,5A5,5,0,0,1,823.32,443.53Zm15.88,11.22V454l10.88-2.67v5.39a12.93,12.93,0,0,1,9.71-4.61c6.5,0,9.71,4.85,9.71,11.5V478h1.16v.78h-12V478h1.17V463.49c0-5.69-2.29-7.34-5.3-7.34a6.49,6.49,0,0,0-4.41,1.51V478h1.16v.78h-12V478h1.16v-23.5ZM888.87,493v.77c-7.82,0-14.32-2.76-14.32-7.91,0-4.52,6.16-6.17,9.46-6.17a4.69,4.69,0,0,1,.88.05v.63c-1.17.49-3,2.19-3,5.49C881.93,489.75,884.4,493,888.87,493Zm8-7.14c0,3.84-2.62,7.14-7.67,7.14v.77c9.66,0,15.59-4.71,15.59-10,0-13.54-26.22-8-26.22-14a3.35,3.35,0,0,1,.39-1.41c2.57,1.9,6.4,2.87,10.58,2.87,7.43,0,13.79-3,13.79-9v-1c0-3.78-2.62-6.41-6.46-7.76a7.18,7.18,0,0,1-.58-1.66,4,4,0,0,0,2.38.78,3.85,3.85,0,0,0,3.83-3.88,3.86,3.86,0,0,0-7.72,0,13,13,0,0,0,.59,4.32,22.39,22.39,0,0,0-5.83-.73c-7.38,0-13.69,3-13.69,8.93v1a7.29,7.29,0,0,0,1.89,5,6.5,6.5,0,0,0-1.45,3.78C876.25,481.3,896.83,477.81,896.83,485.82Zm-4.13-22.63c0,4.71-.87,7.19-3.2,7.19s-3.16-2.48-3.16-7.19v-3c0-4.71.93-7.18,3.16-7.18s3.2,2.47,3.2,7.18Zm21.66-26.46a5.5,5.5,0,0,1,5.44,5.44v.24c-.1,7.58-4.62,25.74-4.62,25.74h-1.6s-4.66-18.55-4.66-25.93v-.05A5.5,5.5,0,0,1,914.36,436.73Zm.14,42.34a4.23,4.23,0,1,1,4.28-4.17A4.14,4.14,0,0,1,914.5,479.07Z' />
				</g>

				<rect
					y='433.3'
					x='293.71'
					height='62.36'
					width='630.99'
					style={{ opacity: 0 }}
					className={`star-group`}
					onMouseEnter={() => animateStar()}
					onMouseLeave={() => animateStar(true)}
				/>
			</svg>
		</TextHoverAnimationsStyle>
	);
});

export default TextHoverAnimations;
